import { fetcher } from '@data/utils/api/fetcher/fetcher';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useApplications } from '@data/hooks/api/use-applications-api/use-applications-api';
import { userShape, type User } from 'eds-common/api/v3';
import { z } from 'zod';

export interface UseGetStudentsByClassAndEdAppApiParams {
  classId: string;
  edApp: string;
}

async function getStudentsByClassAndEdApp({
  classId,
  productCodes,
}: {
  classId: string;
  productCodes: string[];
}): Promise<User[]> {
  const students = await fetcher<User[]>(
    `/users/classes/${classId}/user-types/STUDENT`,
    {
      service: '/roster',
      version: '/v3',
      queryParameters: { productCodes },
    }
  );

  return z.array(userShape).parse(students);
}

export function useGetStudentsByClassAndEdAppApi(
  { classId, edApp }: UseGetStudentsByClassAndEdAppApiParams,
  customQueryOptions?: Partial<UseQueryOptions<User[]>>
) {
  const { data: applications } = useApplications();

  const productCodes =
    applications
      ?.find((app) => app.applicationCode === edApp)
      ?.products.map((product) => product.productCode) ?? [];

  const { enabled = true } = customQueryOptions ?? {};

  return useQuery<User[]>({
    queryKey: ['students-by-class-and-ed-app', classId, edApp, productCodes],
    queryFn: () => getStudentsByClassAndEdApp({ classId, productCodes }),
    enabled: enabled && Boolean(classId && edApp) && productCodes.length > 0,
    ...customQueryOptions,
  });
}
